import { useState } from 'react';
import { useCan } from '@/hooks/use-scopes';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Copy, Send, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { fetchClients, setUserAccess, qk } from '@/lib/queries';
import Select from '@/components/ui/Select';
import { cn } from '@/lib/cn';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Navigate } from 'react-router-dom';
import { DESIGNATIONS, ROLES, type Role, type Designation } from '@shared/enums';

interface InviteResponse {
  user: { id: string; email: string; display_name: string; role: Role };
  invite_token: string;
  invite_url: string;
  expires_in_days: number;
}

export default function InviteUserPage() {
  const allowed = useCan('users.invite');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('crew');
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [accessLevel, setAccessLevel] = useState<'tasks' | 'clients'>('tasks');
  const [defaultCrew, setDefaultCrew] = useState(false);
  const [result, setResult] = useState<InviteResponse | null>(null);
  const clinics = useQuery({ queryKey: qk.clients, queryFn: fetchClients });

  const invite = useMutation({
    mutationFn: async (): Promise<InviteResponse> => {
      const r = await api.post<InviteResponse>('/users', {
        email: email.trim(),
        display_name: name.trim(),
        role,
        designations,
      });
      // A person with no clinic sees nothing, so set their access in the same breath.
      if (role !== 'owner' && role !== 'manager' && (clientIds.length || accessLevel !== 'tasks' || defaultCrew)) {
        await setUserAccess(r.data.user.id, clientIds, accessLevel, defaultCrew);
      }
      return r.data;
    },
    onSuccess: (data) => {
      setResult(data);
      toast.success(`Invited ${data.user.display_name}`);
    },
  });

  if (!allowed) return <Navigate to="/" replace />;

  function reset() {
    setEmail(''); setName(''); setRole('crew'); setDesignations([]); setClientIds([]); setAccessLevel('tasks'); setDefaultCrew(false); setResult(null);
  }

  function toggleDesignation(d: Designation) {
    setDesignations((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));
  }

  async function copyLink() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.invite_url);
      toast.success('Invite link copied');
    } catch {
      toast.error('Copy failed - select the link manually');
    }
  }

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Invite a teammate"
        subtitle="They can sign in with Google OR open the magic link directly - no Google account needed."
      />

      {!result && (
        <Card className="space-y-5">
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kareem@kaf4media.com" />
          <Input label="Display name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Kareem Khabier" />

          <div className="space-y-2">
            <label className="text-xs text-muted">Role</label>
            <div className="flex flex-wrap gap-2">
              {ROLES.filter((r) => r !== 'owner').map((r) => (
                <button
                  key={r} type="button" onClick={() => setRole(r)}
                  className={
                    'text-xs px-3 py-1.5 rounded-full border transition capitalize ' +
                    (role === r
                      ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                      : 'border-slate-800 text-muted hover:text-muted-strong')
                  }
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs text-muted">Designations (optional, multi-select)</label>
            <div className="flex flex-wrap gap-2">
              {DESIGNATIONS.map((d) => (
                <button
                  key={d} type="button" onClick={() => toggleDesignation(d)}
                  className={
                    'text-[11px] px-2.5 py-1 rounded-full border transition ' +
                    (designations.includes(d)
                      ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                      : 'border-slate-800 text-muted hover:text-muted-strong')
                  }
                >
                  {d.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          {role !== 'owner' && role !== 'manager' && role !== 'client' && (
            <div className="space-y-2 border-t border-slate-800 pt-4">
              <label className="text-xs text-muted">Clinics they work on</label>
              <p className="text-[11px] text-muted">Without a clinic a new person signs in and sees nothing, so pick at least one.</p>
              <div className="flex flex-wrap gap-2">
                {(clinics.data ?? []).map((c) => (
                  <button key={c.id} type="button" onClick={() => setClientIds((cur) => cur.includes(c.id) ? cur.filter((x) => x !== c.id) : [...cur, c.id])}
                    className={cn('text-[11px] px-2.5 py-1.5 min-h-[32px] rounded-full border transition',
                      clientIds.includes(c.id) ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted hover:text-muted-strong')}>
                    {c.name}
                  </button>
                ))}
                {(clinics.data ?? []).length === 0 && <span className="text-[11px] text-muted">No clinics yet.</span>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <Select label="How much of the clinic they see" value={accessLevel} onChange={(v) => setAccessLevel(v as 'tasks' | 'clients')}
                  options={[{ value: 'tasks', label: 'Only the work assigned to them' }, { value: 'clients', label: 'Everything on their clinics' }]} />
                <label className="inline-flex items-center gap-2 text-xs text-muted self-end min-h-[36px]">
                  <input type="checkbox" className="accent-accent-400 w-4 h-4" checked={defaultCrew} onChange={(e) => setDefaultCrew(e.target.checked)} />
                  Put them on every new clinic automatically
                </label>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              icon={<Send size={14} />}
              onClick={() => invite.mutate()}
              loading={invite.isPending}
              disabled={!email || !name}
            >
              Generate invite link
            </Button>
          </div>
        </Card>
      )}

      {result && (
        <Card className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent-600/20 text-accent-400">
              <UserPlus size={18} />
            </div>
            <div>
              <div className="font-medium text-muted-strong">
                {result.user.display_name} <span className="text-muted text-xs">&lt;{result.user.email}&gt;</span>
              </div>
              <div className="text-xs text-muted">
                Role: <span className="text-accent-400 capitalize">{result.user.role}</span>
                &middot; Link expires in {result.expires_in_days} days
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs text-muted">Magic-link URL (share via WhatsApp / email)</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={result.invite_url}
                onClick={(e) => e.currentTarget.select()}
                className="flex-1 h-10 rounded-lg border border-slate-800 bg-bg px-3 text-xs text-muted-strong font-mono"
              />
              <Button icon={<Copy size={14} />} onClick={copyLink}>Copy</Button>
            </div>
            <p className="text-[11px] text-muted">
              Anyone who opens this link is automatically signed in as {result.user.display_name}. Keep it private.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
            <Button variant="ghost" onClick={reset}>Invite another</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
